/** Limite de paradas no OSRM Table/Route (origem do profissional ocupa 1 das 100). */
export const LIMITE_ENDERECOS_OSRM = 99

export function coordValida(la, lo) {
	const lat = Number(la)
	const lon = Number(lo)
	return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)
}

export function pontoFinalizado(end) {
	const saida = end?.["es.saida"]
	if (saida == null || saida === "") return false
	if (saida === "0000-00-00 00:00:00") return false
	return true
}

/** Pitágoras no plano, eixos em km (longitude corrigida pelo cosseno da latitude). */
export function distanciaPlanoKm(prof, ponto) {
	const dLat = (Number(ponto.la) - Number(prof.la)) * 111.32
	const dLon =
		(Number(ponto.lo) - Number(prof.lo)) *
		111.32 *
		Math.cos((Number(prof.la) * Math.PI) / 180)
	return Math.hypot(dLat, dLon)
}

export function headingEntre(de, para) {
	const dLon = ((para.lo - de.lo) * Math.PI) / 180
	const lat1 = (de.la * Math.PI) / 180
	const lat2 = (para.la * Math.PI) / 180
	const y = Math.sin(dLon) * Math.cos(lat2)
	const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
	return (Math.atan2(y, x) * 180) / Math.PI
}

export function headingNormalizado(graus) {
	return ((graus % 360) + 360) % 360
}

/** Mantém o ângulo contínuo para o CSS interpolar pelo caminho mais curto (ex.: 350° → 370° em vez de voltar). */
export function headingContinuo(atual, alvo, morto = 3, passo = 40) {
	const orig = Number(atual) || 0
	const dest = headingNormalizado(alvo)
	let d = dest - headingNormalizado(orig)
	if (d > 180) d -= 360
	if (d < -180) d += 360
	if (Math.abs(d) < morto) return orig
	if (d > passo) d = passo
	if (d < -passo) d = -passo
	return orig + d
}

export const MINIMO_MOVIMENTO_HEADING_KM = 0.012
export const MINIMO_CURSO_GPS_KM = 0.028
export const DESVIO_ROTA_KM = 0.06
export const OLHAR_FRENTE_ROTA_KM = 0.04
export const HEADING_MORTO_ROTA = 10
export const HEADING_PASSO_ROTA = 28
export const HEADING_MORTO_PARADO = 18
export const HEADING_PASSO_PARADO = 8
export const BUSSOLA_SILENCIO_MS = 4000

/** Rumo da polyline à frente (~40 m), no estilo do GPS em navegação. */
export function headingDaRota(pos, line, olharKm = OLHAR_FRENTE_ROTA_KM) {
	const proj = projetoNaPolyline(pos, line)
	if (!proj || proj.distKm > DESVIO_ROTA_KM) return null
	let rest = Number(olharKm) > 0 ? olharKm : 0
	let from = { la: proj.la, lo: proj.lo }
	for (let i = proj.index; i < line.length - 1; i++) {
		const to = latLngDaLinha(line[i + 1])
		if (!coordValida(to.la, to.lo)) continue
		const d = distanciaPlanoKm(from, to)
		if (d >= rest || i === line.length - 2) {
			if (!coordValida(from.la, from.lo) || d < 0.002) break
			return headingNormalizado(headingEntre(from, to))
		}
		rest -= d
		from = to
	}
	const a = latLngDaLinha(line[proj.index])
	const b = latLngDaLinha(line[Math.min(proj.index + 1, line.length - 1)])
	if (!coordValida(a.la, a.lo) || !coordValida(b.la, b.lo)) return null
	return headingNormalizado(headingEntre(a, b))
}

export function escolherHeading(pos, anterior, _proximo, opcoes = {}) {
	const andando = anterior && pos && deslocouOSuficiente(anterior, pos, MINIMO_MOVIMENTO_HEADING_KM)
	if (andando) {
		const rota = headingDaRota(pos, opcoes.line)
		if (rota != null) return rota
		if (deslocouOSuficiente(anterior, pos, MINIMO_CURSO_GPS_KM)) {
			return headingNormalizado(headingEntre(anterior, pos))
		}
		return null
	}
	if (opcoes.usarBussola === false) return null
	const bussola = Number(opcoes.bussola)
	if (Number.isFinite(bussola)) return headingNormalizado(bussola)
	const dispositivo = Number(pos?.heading)
	if (Number.isFinite(dispositivo) && dispositivo >= 0) return dispositivo
	return null
}

export function deltaHeadingAbs(a, b) {
	let d = headingNormalizado(a) - headingNormalizado(b)
	if (d > 180) d -= 360
	if (d < -180) d += 360
	return Math.abs(d)
}

export function saiuDaRota(pos, line, heading) {
	const proj = projetoNaPolyline(pos, line)
	if (!proj) return true
	if (proj.distKm > DESVIO_ROTA_KM) return true
	if (!Number.isFinite(Number(heading)) || !Array.isArray(line) || line.length < 2) return false
	const a = latLngDaLinha(line[proj.index])
	const b = latLngDaLinha(line[Math.min(proj.index + 1, line.length - 1)])
	if (!coordValida(a.la, a.lo) || !coordValida(b.la, b.lo)) return false
	return deltaHeadingAbs(heading, headingEntre(a, b)) > 110
}

export function juntarTrechoComResto(trechoNovo, lineAntiga, proximo) {
	const novo = Array.isArray(trechoNovo) ? trechoNovo : []
	if (!novo.length) return Array.isArray(lineAntiga) ? lineAntiga : []
	const proj = projetoNaPolyline(proximo, lineAntiga)
	if (!proj || !Array.isArray(lineAntiga)) return novo
	const resto = lineAntiga.slice(proj.index).map((pt) => {
		const p = latLngDaLinha(pt)
		return [p.la, p.lo]
	})
	return resto.length > 1 ? [...novo, ...resto.slice(1)] : novo
}

function xyKm(origem, p) {
	return {
		x: (Number(p.lo) - Number(origem.lo)) * 111.32 * Math.cos((Number(origem.la) * Math.PI) / 180),
		y: (Number(p.la) - Number(origem.la)) * 111.32,
	}
}

function latLngDaLinha(pt) {
	if (Array.isArray(pt)) return { la: Number(pt[0]), lo: Number(pt[1]) }
	return { la: Number(pt?.la), lo: Number(pt?.lo) }
}

export function projetoNaPolyline(pos, line) {
	if (!pos || !coordValida(pos.la, pos.lo) || !Array.isArray(line) || line.length < 2) return null
	let melhor = null
	for (let i = 0; i < line.length - 1; i++) {
		const a = latLngDaLinha(line[i])
		const b = latLngDaLinha(line[i + 1])
		if (!coordValida(a.la, a.lo) || !coordValida(b.la, b.lo)) continue
		const pb = xyKm(a, b)
		const pq = xyKm(a, pos)
		const ab2 = pb.x * pb.x + pb.y * pb.y
		let t = ab2 === 0 ? 0 : (pq.x * pb.x + pq.y * pb.y) / ab2
		if (t < 0) t = 0
		if (t > 1) t = 1
		const distKm = Math.hypot(pq.x - pb.x * t, pq.y - pb.y * t)
		if (!melhor || distKm < melhor.distKm) {
			melhor = {
				distKm,
				index: i,
				la: a.la + (b.la - a.la) * t,
				lo: a.lo + (b.lo - a.lo) * t,
			}
		}
	}
	return melhor
}

export function recortarPolylineFrente(pos, line) {
	if (!Array.isArray(line) || line.length < 2) return Array.isArray(line) ? line : []
	const proj = projetoNaPolyline(pos, line)
	if (!proj) return line
	const frente = [[proj.la, proj.lo], ...line.slice(proj.index + 1).map((pt) => {
		const p = latLngDaLinha(pt)
		return [p.la, p.lo]
	})]
	return frente.length >= 2 ? frente : line
}

export function comprimentoPolylineKm(line) {
	if (!Array.isArray(line) || line.length < 2) return 0
	let km = 0
	for (let i = 0; i < line.length - 1; i++) {
		const a = latLngDaLinha(line[i])
		const b = latLngDaLinha(line[i + 1])
		if (coordValida(a.la, a.lo) && coordValida(b.la, b.lo)) km += distanciaPlanoKm(a, b)
	}
	return km
}

/** Trecho da linha (já recortada na frente do GPS) até o próximo pin. */
export function polylineAtePonto(line, ponto) {
	if (!Array.isArray(line) || line.length < 2 || !ponto) return Array.isArray(line) ? line : []
	const proj = projetoNaPolyline(ponto, line)
	if (!proj) return line
	const ate = []
	for (let i = 0; i <= proj.index; i++) {
		const p = latLngDaLinha(line[i])
		ate.push([p.la, p.lo])
	}
	ate.push([proj.la, proj.lo])
	return ate.length >= 2 ? ate : line
}

/** Resto da linha a partir do pin (próximas paradas). */
export function polylineAposPonto(line, ponto) {
	if (!Array.isArray(line) || line.length < 2 || !ponto) return []
	const proj = projetoNaPolyline(ponto, line)
	if (!proj) return []
	const resto = [[proj.la, proj.lo], ...line.slice(proj.index + 1).map((pt) => {
		const p = latLngDaLinha(pt)
		return [p.la, p.lo]
	})]
	return resto.length >= 2 ? resto : []
}

const VELOCIDADE_FALLBACK_KMH = 22

/** Tempo restante pela fração da linha, usando a velocidade média da consulta inicial. */
export function etaAoLongoDaLinha(kmRestante, kmOriginal, segOriginal) {
	const km = Math.max(0, Number(kmRestante) || 0)
	if (km < 0.008) return { km: 0, seg: 0 }
	const km0 = Number(kmOriginal)
	const t0 = Number(segOriginal)
	if (Number.isFinite(km0) && km0 > 0.02 && Number.isFinite(t0) && t0 > 0) {
		return { km, seg: km * (t0 / km0) }
	}
	return { km, seg: (km / VELOCIDADE_FALLBACK_KMH) * 3600 }
}

export function etaAteProximo(pos, line, proximo, metricas = {}) {
	if (!proximo || !Array.isArray(line) || line.length < 2) return null
	const frente = recortarPolylineFrente(pos, line)
	const trecho = polylineAtePonto(frente, proximo)
	return etaAoLongoDaLinha(
		comprimentoPolylineKm(trecho),
		metricas.distanciaProximoKm,
		metricas.duracaoProximoSeg,
	)
}

/** Distância bruta em graus² — para ignorar ruído de GPS parado. */
export function deslocouOSuficiente(de, para, minimoKm = 0.008) {
	if (!de || !para) return false
	return distanciaPlanoKm(de, para) >= minimoKm
}

export const MODOS_ROTA = {
	COLETAS: "coletas",
	LIBERADAS: "liberadas",
	TODAS: "todas",
}

export function tipoPontoDeEndereco(end) {
	return Number(end?.["es.ponto"]) === 1 ? "coleta" : "entrega"
}

export function coletaDoServicoFinalizada(servico) {
	const enderecos = servico?.enderecos
	if (!Array.isArray(enderecos) || !enderecos.length) return false
	const coleta = enderecos.find((e) => Number(e["es.ponto"]) === 1) || enderecos[0]
	return pontoFinalizado(coleta)
}

function montarPontoRota(servico, end) {
	return {
		key: `${servico["s.id"]}-${end["e.id"]}`,
		idServico: servico["s.id"],
		idEndereco: end["e.id"],
		la: Number(end["e.la"]),
		lo: Number(end["e.lo"]),
		rua: end["e.rua"] || "",
		complemento: end["e.complemento"] || "",
		telefone: end["e.telefone"] || "",
		contato: end["e.contato"] || "",
		observacao: end["e.responsavel"] || "",
		ponto: end["es.ponto"],
		tipoPonto: tipoPontoDeEndereco(end),
		codigoIfood: end.codigoLocalizadorIfood,
		urgente: servico["s.urgente"] == "S",
		nomeCliente: servico.nome || "",
		valor: servico["s.valorProfissional"] ?? servico["s.valor"],
		distanciaServico: servico["s.distancia"],
		status: servico.filtroLegenda || "",
		filtroServico: servico.filtro || "",
		ifood: servico["s.ifood"],
		tipo: servico["s.tipo"],
	}
}

/**
 * `coletas`: só ponto 1 em aberto.
 * `liberadas`: coletas pendentes + entregas de OS cuja coleta já foi feita.
 * `todas`: todo ponto em aberto.
 */
export function filtrarEnderecosParaRota(servicos, modo = MODOS_ROTA.LIBERADAS, idsServicos = []) {
	if (!Array.isArray(servicos)) return []
	const ids = Array.isArray(idsServicos) && idsServicos.length
		? new Set(idsServicos.map(String))
		: null
	const lista = []

	for (const servico of servicos) {
		const idServico = String(servico?.["s.id"] ?? "")
		if (ids && !ids.has(idServico)) continue
		const enderecos = servico?.enderecos
		if (!Array.isArray(enderecos)) continue
		const coletaFeita = coletaDoServicoFinalizada(servico)

		for (const end of enderecos) {
			if (pontoFinalizado(end)) continue
			if (!coordValida(end["e.la"], end["e.lo"])) continue
			const tipoPonto = tipoPontoDeEndereco(end)
			if (modo === MODOS_ROTA.COLETAS && tipoPonto !== "coleta") continue
			if (modo === MODOS_ROTA.LIBERADAS && tipoPonto === "entrega" && !coletaFeita) continue
			lista.push(montarPontoRota(servico, end))
		}
	}

	return lista
}

export function extrairEnderecosAbertos(servicos) {
	return filtrarEnderecosParaRota(servicos, MODOS_ROTA.TODAS)
}

export function listarServicosRota(servicos) {
	if (!Array.isArray(servicos)) return []
	const lista = []

	for (const servico of servicos) {
		const enderecos = Array.isArray(servico?.enderecos) ? servico.enderecos : []
		let qtdColeta = 0
		let qtdEntrega = 0
		for (const end of enderecos) {
			if (pontoFinalizado(end) || !coordValida(end["e.la"], end["e.lo"])) continue
			if (tipoPontoDeEndereco(end) === "coleta") qtdColeta++
			else qtdEntrega++
		}
		if (!qtdColeta && !qtdEntrega) continue
		const coletaFeita = coletaDoServicoFinalizada(servico)
		lista.push({
			id: servico["s.id"],
			nome: servico.nome || "",
			status: servico.filtroLegenda || (coletaFeita ? "A Finalizar" : "A Coletar"),
			filtro: servico.filtro || (coletaFeita ? "aFinalizar" : "aColetar"),
			coletaFeita,
			qtdColeta,
			qtdEntrega,
		})
	}

	return lista
}

export function contarTiposPonto(pontos) {
	let coletas = 0
	let entregas = 0
	for (const p of pontos || []) {
		if (p?.tipoPonto === "coleta") coletas++
		else entregas++
	}
	return { coletas, entregas }
}

export function resumirParadas(pontos) {
	if (!Array.isArray(pontos) || !pontos.length) return ""
	const { coletas, entregas } = contarTiposPonto(pontos)
	const n = pontos.length
	const base = `${n} parada${n > 1 ? "s" : ""}`
	if (!coletas && !entregas) return base
	const partes = []
	if (coletas) partes.push(`${coletas}C`)
	if (entregas) partes.push(`${entregas}E`)
	return `${base} · ${partes.join(" · ")}`
}

export function enderecosMaisProximos(prof, enderecos, limite = LIMITE_ENDERECOS_OSRM) {
	if (!prof || !coordValida(prof.la, prof.lo)) return []
	if (!Array.isArray(enderecos) || !enderecos.length) return []

	return enderecos
		.map((e) => ({ ...e, distKm: distanciaPlanoKm(prof, e) }))
		.sort((a, b) => a.distKm - b.distKm)
		.slice(0, limite)
}

export function formatarDuracao(segundos) {
	const s = Number(segundos)
	if (!Number.isFinite(s) || s < 0) return ""
	if (s < 60) return "< 1 min"
	const m = Math.round(s / 60)
	if (m < 60) return `${m} min`
	const h = Math.floor(m / 60)
	const mm = m % 60
	return mm ? `${h} h ${mm} min` : `${h} h`
}

export function formatarKm(km) {
	const n = Number(km)
	if (!Number.isFinite(n) || n < 0) return ""
	if (n < 1) return `${Math.round(n * 1000)} m`
	return `${n.toFixed(1).replace(".", ",")} km`
}

export function formatarValor(valor) {
	const n = Number(String(valor ?? "").replace(",", "."))
	if (!Number.isFinite(n)) return ""
	return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n)
}

export function numerarParadas(pontos) {
	return (Array.isArray(pontos) ? pontos : []).map((p, i) => ({ ...p, parada: i + 1 }))
}

export function trocarParadas(pontos, keyA, keyB) {
	const lista = [...(pontos || [])]
	const i = lista.findIndex((p) => p.key === keyA)
	const j = lista.findIndex((p) => p.key === keyB)
	if (i < 0 || j < 0 || i === j) return numerarParadas(lista)
	const tmp = lista[i]
	lista[i] = lista[j]
	lista[j] = tmp
	return numerarParadas(lista)
}
